import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { api, API, getToken, formatApiErrorDetail } from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import UniversalDocumentViewer from "@/components/documents/UniversalDocumentViewer";
import CanonicalUploader from "@/components/documents/CanonicalUploader";
import { toast } from "sonner";

const STATUS_TONE = {
  awaiting_review: "bg-amber-100 text-amber-700", needs_revision: "bg-orange-100 text-orange-700",
  approved: "bg-emerald-100 text-emerald-700", rejected: "bg-rose-100 text-rose-700",
  superseded: "bg-slate-100 text-slate-400", archived: "bg-slate-100 text-slate-400", uploaded: "bg-blue-100 text-blue-700",
};
const TAX_YEARS = Array.from({ length: 2026 - 2009 }, (_, i) => String(2026 - i)); // 2026…2010
const YEAR_CHIP_LABEL = { "__na__": "N/A", "__unknown__": "Unknown", "__unconfirmed__": "Unconfirmed" };
const fmtDT = (d) => (d ? new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—");
const prettySize = (b) => { if (!b && b !== 0) return "—"; const u = ["B", "KB", "MB", "GB"]; let i = 0, v = b; while (v >= 1024 && i < 3) { v /= 1024; i++; } return `${v.toFixed(v < 10 && i > 0 ? 1 : 0)} ${u[i]}`; };

function Widget({ icon, label, value, tone }) {
  const Ic = Icons[icon];
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 flex items-center gap-3">
      <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${tone}`}><Ic className="h-5 w-5" /></div>
      <div><div className="text-2xl font-bold text-slate-900 leading-none">{value}</div><div className="text-xs text-slate-500 mt-1">{label}</div></div>
    </div>
  );
}

function ClsBadge({ doc }) {
  if (doc.display_name_locked) return (
    <span className="inline-flex items-center gap-0.5 text-[9px] px-1 py-0.5 rounded bg-emerald-100 text-emerald-700 ml-1" title="Display name confirmed by staff">
      <Icons.Lock className="h-2.5 w-2.5" />Named
    </span>
  );
  if (doc.display_name_suggested && !doc.display_name_locked) return (
    <span className="inline-flex items-center gap-0.5 text-[9px] px-1 py-0.5 rounded bg-violet-100 text-violet-700 ml-1" title="AI suggested a display name — review in drawer">
      <Icons.Sparkles className="h-2.5 w-2.5" />Suggested
    </span>
  );
  return null;
}

const STR_STATE_ICONS = {
  uploaded: { icon: "Upload", tone: "text-slate-400", label: "Uploaded" },
  ocr_complete: { icon: "ScanText", tone: "text-blue-500", label: "OCR" },
  classified: { icon: "Tag", tone: "text-indigo-500", label: "Classified" },
  metadata_extracted: { icon: "BrainCircuit", tone: "text-violet-600", label: "Metadata" },
  organizer_match_suggested: { icon: "Link", tone: "text-orange-500", label: "Match Suggested" },
  str_evidence_tagged: { icon: "Flame", tone: "text-rose-500", label: "STR Evidence" },
  business_reviewed: { icon: "CheckCircle2", tone: "text-emerald-500", label: "Reviewed" },
  production_ready: { icon: "Rocket", tone: "text-emerald-600", label: "Production Ready" },
};

function IntelligenceStateBadge({ state }) {
  const cfg = STR_STATE_ICONS[state] || STR_STATE_ICONS.uploaded;
  const Ic = Icons[cfg.icon];
  return (
    <span className={`inline-flex items-center gap-0.5 text-[9px] px-1 py-0.5 rounded bg-slate-50 border border-slate-200 ml-1 ${cfg.tone}`} title={`Intelligence: ${cfg.label}`}>
      <Ic className="h-2.5 w-2.5" />{cfg.label}
    </span>
  );
}

function STRBadge({ doc }) {
  if (!doc.str_evidence) return null;
  const count = (doc.str_evidence_categories || []).length;
  return (
    <span className="inline-flex items-center gap-0.5 text-[9px] px-1 py-0.5 rounded bg-rose-100 text-rose-700 ml-1" title={`STR Evidence (${count} categories)`}>
      <Icons.Flame className="h-2.5 w-2.5" />STR
    </span>
  );
}

export default function Documents() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [filters, setFilters] = useState({ status: "", category: "", source: "", tax_year: "" });
  const [advFilters, setAdvFilters] = useState({ doc_type: "", jurisdiction: "", classification_status: "", sort_by: "" });
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [page, setPage] = useState(1);
  const [categories, setCategories] = useState([]);
  const [preview, setPreview] = useState(null);
  const [detail, setDetail] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const searchTimer = useRef(null);

  // Bulk select state
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkAction, setBulkAction] = useState("");
  const [bulkMeta, setBulkMeta] = useState({});
  const [bulkLoading, setBulkLoading] = useState(false);

  // Saved views state
  const [savedViews, setSavedViews] = useState({ personal: [], shared: [] });
  const [savedViewsOpen, setSavedViewsOpen] = useState(false);
  const [saveViewName, setSaveViewName] = useState("");
  const [saveViewScope, setSaveViewScope] = useState("personal");
  const [savingView, setSavingView] = useState(false);

  // Allison re-run state
  const [allisonRunning, setAllisonRunning] = useState(false);
  const [allisonResult, setAllisonResult] = useState(null);

  // Organizer match suggestions (loaded in drawer)
  const [matchSuggestions, setMatchSuggestions] = useState([]);
  const [matchLoading, setMatchLoading] = useState(false);
  const [suggestion, setSuggestion] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [renameMode, setRenameMode] = useState(false);

  const [loadError, setLoadError] = useState(null); // null | "forbidden" | "error"

  const load = useCallback(async () => {
    setLoadError(null);
    const params = new URLSearchParams({ page, page_size: 30, include_archived: includeArchived });
    if (q) params.set("q", q);
    Object.entries(filters).forEach(([k, v]) => v && params.set(k, v));
    Object.entries(advFilters).forEach(([k, v]) => v && params.set(k === "doc_type" ? "doc_type" : k, v));
    try {
      const { data } = await api.get(`/documents-intel/browse/all?${params}`);
      setData(data);
    } catch (e) {
      const status = e?.response?.status;
      setData(null);
      setLoadError(status === 403 || status === 401 ? "forbidden" : "error");
    }
  }, [q, filters, advFilters, includeArchived, page]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const TERMINAL = ["ready", "partially_ready", "quarantined", "pending_security_review", "processing_failed"];
    const anyProcessing = (data?.items || []).some((d) => d.processing_status && !TERMINAL.includes(d.processing_status));
    if (!anyProcessing) return;
    const t = setTimeout(() => load(), 4000);
    return () => clearTimeout(t);
  }, [data, load]);

  useEffect(() => { api.get("/documents-intel/categories").then((r) => setCategories(r.data.categories)).catch(() => {}); }, []);

  useEffect(() => {
    if (!preview?.id) { setDetail(null); setSuggestion(null); setRenameMode(false); setAllisonResult(null); setMatchSuggestions([]); return; }
    setDetail(null); setSuggestion(null); setRenameMode(false); setAllisonResult(null);
    setRenameValue(preview.display_filename || "");
    api.get(`/documents-intel/${preview.id}`).then((r) => setDetail(r.data)).catch(() => {});
    loadMatchSuggestions(preview.id);
    if (preview.display_name_suggested && !preview.display_name_locked) {
      setSuggestion({ suggested_name: preview.display_name_suggested });
    }
  }, [preview]);

  const loadSavedViews = useCallback(async () => {
    try { const { data } = await api.get("/documents-intel/saved-views"); setSavedViews(data); }
    catch { setSavedViews({ personal: [], shared: [] }); }
  }, []);
  useEffect(() => { loadSavedViews(); }, [loadSavedViews]);

  const w = data?.widgets || {};
  const items = data?.items || [];
  const allIds = items.map((d) => d.id);
  const allSelected = allIds.length > 0 && allIds.every((id) => selectedIds.has(id));

  const toggleSelect = (id) => setSelectedIds((prev) => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const selectAll = () => setSelectedIds(new Set(allIds));
  const clearSelection = () => setSelectedIds(new Set());

  const rename = async (doc) => {
    const name = window.prompt("New display name:", doc.display_filename);
    if (!name?.trim()) return;
    try { await api.patch(`/documents-intel/${doc.id}/metadata`, { display_filename: name.trim() }); toast.success("Renamed and locked"); load(); setPreview(null); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const archive = async (doc) => {
    if (!window.confirm("Archive this document?")) return;
    try { await api.post(`/documents-intel/${doc.id}/archive`); toast.success("Archived"); load(); setPreview(null); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const changeCategory = async (doc, category) => {
    try { await api.patch(`/documents-intel/${doc.id}/metadata`, { category }); toast.success("Category updated"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const suggestDisplayName = async () => {
    if (!preview?.id) return;
    setAllisonRunning(true);
    try {
      const { data } = await api.post(`/documents-intel/${preview.id}/suggest-display-name`);
      setSuggestion(data);
      if (data.suggested_name) toast.success("AI suggestion ready — review below");
    } catch (e) {
      const msg = e.response?.data?.detail;
      if (msg?.includes("locked")) toast.info("Display name is locked by staff");
      else toast.error(formatApiErrorDetail(msg) || "Could not generate suggestion");
    } finally { setAllisonRunning(false); }
  };

  const runAllisonIntelligence = async () => {
    if (!preview?.id) return;
    setAllisonRunning(true);
    try {
      const { data } = await api.post(`/documents-intel/run-allison-intelligence/${preview.id}`);
      setAllisonResult(data);
      toast.success(`Allison intelligence complete — ${Object.keys(data.metadata_extracted || {}).length} fields extracted${data.str_evidence ? "; STR evidence detected" : ""}${data.organizer_match_triggered ? "; organizer match suggested" : ""}`);
      load();
      // Refresh the drawer data
      const { data: updatedDoc } = await api.get(`/documents-intel/${preview.id}`);
      setDetail(updatedDoc);
      // Reload match suggestions
      loadMatchSuggestions(preview.id);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Intelligence run failed"); }
    finally { setAllisonRunning(false); }
  };

  const loadMatchSuggestions = async (docId) => {
    if (!docId) return;
    setMatchLoading(true);
    try {
      const { data } = await api.get(`/documents-intel/${docId}/requirement-matches`);
      setMatchSuggestions((data.matches || []).filter((m) => m.status === "suggested"));
    } catch { setMatchSuggestions([]); }
    finally { setMatchLoading(false); }
  };

  const confirmMatch = async (docId, requestId) => {
    try {
      await api.post(`/documents-intel/${docId}/requirement-matches/${requestId}/confirm`, {});
      toast.success("Organizer match confirmed");
      loadMatchSuggestions(docId);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const rejectMatch = async (docId, requestId) => {
    try {
      await api.post(`/documents-intel/${docId}/requirement-matches/${requestId}/reject`, { reason: "staff_dismissed" });
      toast.success("Match dismissed");
      loadMatchSuggestions(docId);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const acceptSuggestion = async () => {
    if (!preview?.id || !suggestion?.suggested_name) return;
    try {
      await api.patch(`/documents-intel/${preview.id}/metadata`, { display_filename: suggestion.suggested_name });
      toast.success("Display name accepted and locked");
      setSuggestion(null);
      load();
      setPreview((p) => ({ ...p, display_filename: suggestion.suggested_name, display_name_locked: true }));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const saveRename = async () => {
    if (!preview?.id || !renameValue.trim()) return;
    try {
      await api.patch(`/documents-intel/${preview.id}/metadata`, { display_filename: renameValue.trim() });
      toast.success("Display name saved and locked");
      setRenameMode(false);
      load();
      setPreview((p) => ({ ...p, display_filename: renameValue.trim(), display_name_locked: true }));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const executeBulk = async () => {
    if (!selectedIds.size) return;
    setBulkLoading(true);
    try {
      const payload = { doc_ids: [...selectedIds], action: bulkAction };
      if (bulkAction === "set_metadata") payload.metadata = bulkMeta;
      if (bulkAction === "set_tax_year") {
        if (!bulkMeta.tax_year) { toast.error("Please select a tax year."); setBulkLoading(false); return; }
        // Convert "__na__" sentinel to explicit null (confirmed N/A)
        const taxVal = bulkMeta.tax_year === "__na__" ? null : bulkMeta.tax_year;
        payload.metadata = { tax_year: taxVal };
      }
      const { data } = await api.post("/documents-intel/bulk-update", payload);
      toast.success(`${data.updated} document${data.updated !== 1 ? "s" : ""} updated`);
      clearSelection(); setBulkDialogOpen(false); setBulkMeta({}); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBulkLoading(false); }
  };

  const applySavedView = (view) => {
    const f = view.filters || {};
    setFilters((p) => ({
      ...p,
      status: f.status || "",
      category: f.category || "",
      source: f.source || "",
      tax_year: f.tax_year || "",
    }));
    setAdvFilters((p) => ({
      ...p,
      doc_type: f.doc_type || "",
      jurisdiction: f.jurisdiction || "",
      classification_status: f.classification_status || "",
      sort_by: f.sort_by || "",
    }));
    if (f.q) setQ(f.q);
    setPage(1);
    setSavedViewsOpen(false);
    toast.success(`View "${view.name}" applied`);
  };

  const saveCurrentView = async () => {
    if (!saveViewName.trim()) return;
    setSavingView(true);
    try {
      const viewFilters = {
        ...filters,
        ...advFilters,
        ...(q ? { q } : {}),
      };
      await api.post("/documents-intel/saved-views", { name: saveViewName.trim(), scope: saveViewScope, filters: viewFilters });
      toast.success(`View "${saveViewName.trim()}" saved`);
      setSaveViewName(""); loadSavedViews();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSavingView(false); }
  };

  const deleteSavedView = async (id, name) => {
    if (!window.confirm(`Delete view "${name}"?`)) return;
    try { await api.delete(`/documents-intel/saved-views/${id}`); toast.success("View deleted"); loadSavedViews(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const activeFiltersCount = [
    filters.status, filters.category, filters.source, filters.tax_year,
    advFilters.doc_type, advFilters.jurisdiction, advFilters.classification_status
  ].filter(Boolean).length;

  return (
    <div className="p-6 max-w-[1500px] mx-auto space-y-5" data-testid="documents-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs text-slate-400 uppercase tracking-wide">Document Intelligence</p>
          <h1 className="text-2xl font-bold text-slate-900">Documents</h1>
          <p className="text-sm text-slate-500 mt-0.5">Firm-wide secure document repository — search, bulk metadata, classification and audit.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setSavedViewsOpen((p) => !p)} data-testid="docs-saved-views-btn"
            className="inline-flex items-center gap-1.5 text-sm border border-slate-200 bg-white hover:bg-slate-50 rounded-lg px-3 py-2.5">
            <Icons.BookMarked className="h-4 w-4" />Saved Views
            {(savedViews.personal.length + savedViews.shared.length) > 0 && (
              <span className="ml-0.5 text-[10px] bg-blue-100 text-blue-700 rounded-full px-1.5">{savedViews.personal.length + savedViews.shared.length}</span>
            )}
          </button>
          <button onClick={() => setUploadOpen(true)} data-testid="documents-upload-button"
            className="inline-flex items-center gap-1.5 text-sm bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg px-4 py-2.5">
            <Icons.Upload className="h-4 w-4" />Upload Document
          </button>
        </div>
      </div>

      {/* Saved Views Panel */}
      {savedViewsOpen && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 space-y-3" data-testid="saved-views-panel">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-1.5"><Icons.BookMarked className="h-4 w-4 text-slate-400" />Saved Views</h3>
            <button onClick={() => setSavedViewsOpen(false)} className="p-1 rounded hover:bg-slate-100"><Icons.X className="h-4 w-4 text-slate-400" /></button>
          </div>
          {savedViews.shared.length > 0 && (
            <div>
              <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-1.5">Firm Views</p>
              <div className="flex flex-wrap gap-1.5">
                {savedViews.shared.map((v) => (
                  <div key={v.id} className="inline-flex items-center gap-1 bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5">
                    <button onClick={() => applySavedView(v)} data-testid={`saved-view-${v.id}`}
                      className="text-xs text-slate-700 hover:text-blue-600">{v.name}</button>
                    {!v.is_default && <button onClick={() => deleteSavedView(v.id, v.name)} className="ml-1 text-slate-300 hover:text-rose-500"><Icons.X className="h-3 w-3" /></button>}
                  </div>
                ))}
              </div>
            </div>
          )}
          {savedViews.personal.length > 0 && (
            <div>
              <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-1.5">My Views</p>
              <div className="flex flex-wrap gap-1.5">
                {savedViews.personal.map((v) => (
                  <div key={v.id} className="inline-flex items-center gap-1 bg-violet-50 border border-violet-200 rounded-lg px-2.5 py-1.5">
                    <button onClick={() => applySavedView(v)} data-testid={`saved-view-personal-${v.id}`}
                      className="text-xs text-slate-700 hover:text-violet-700">{v.name}</button>
                    <button onClick={() => deleteSavedView(v.id, v.name)} className="ml-1 text-slate-300 hover:text-rose-500"><Icons.X className="h-3 w-3" /></button>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
            <input value={saveViewName} onChange={(e) => setSaveViewName(e.target.value)}
              placeholder="Save current filters as…" data-testid="save-view-name-input"
              className="flex-1 border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs" />
            <select value={saveViewScope} onChange={(e) => setSaveViewScope(e.target.value)} data-testid="save-view-scope"
              className="border border-slate-200 rounded-lg px-2 py-1.5 text-xs">
              <option value="personal">Personal</option>
              <option value="shared">Firm-wide</option>
            </select>
            <button onClick={saveCurrentView} disabled={savingView || !saveViewName.trim()} data-testid="save-view-btn"
              className="text-xs bg-slate-900 text-white rounded-lg px-3 py-1.5 disabled:opacity-40">
              {savingView ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Widget icon="Files" label="Total Documents" value={w.total ?? "—"} tone="bg-blue-100 text-blue-700" />
        <Widget icon="FileSearch" label="Awaiting Review" value={w.unreviewed ?? w.awaiting_review ?? "—"} tone="bg-amber-100 text-amber-700" />
        <Widget icon="CheckCircle2" label="Accepted" value={w.accepted ?? w.approved ?? "—"} tone="bg-emerald-100 text-emerald-700" />
        <Widget icon="RotateCcw" label="Processing" value={w.processing ?? "—"} tone="bg-orange-100 text-orange-700" />
      </div>

      {/* Main toolbar */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-3 space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[220px]">
            <Icons.Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input defaultValue={q} onChange={(e) => { clearTimeout(searchTimer.current); const v = e.target.value; searchTimer.current = setTimeout(() => { setQ(v); setPage(1); }, 350); }}
              data-testid="documents-search" placeholder="Search name, form #, category, jurisdiction, extracted text…"
              className="w-full bg-slate-50 border border-slate-200 rounded-lg pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200" />
          </div>
          <select value={filters.status} onChange={(e) => { setFilters((p) => ({ ...p, status: e.target.value })); setPage(1); }} data-testid="documents-filter-status"
            className="bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-2 text-sm">
            <option value="">Status: All</option>
            {["awaiting_review", "approved", "needs_revision", "rejected", "superseded"].map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
          </select>
          <select value={filters.category} onChange={(e) => { setFilters((p) => ({ ...p, category: e.target.value })); setPage(1); }} data-testid="documents-filter-category"
            className="bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-2 text-sm">
            <option value="">Category: All</option>
            {categories.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
          <select value={filters.source} onChange={(e) => { setFilters((p) => ({ ...p, source: e.target.value })); setPage(1); }} data-testid="documents-filter-source"
            className="bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-2 text-sm">
            <option value="">Source: All</option>
            {["client_portal", "staff_upload", "organizer", "historical_import"].map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
          </select>
          <select value={filters.tax_year} onChange={(e) => { setFilters((p) => ({ ...p, tax_year: e.target.value })); setPage(1); }} data-testid="documents-filter-year"
            className="bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-2 text-sm">
            <option value="">Tax Year: All</option>
            {TAX_YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
            <option value="__na__">N/A</option>
            <option value="__unknown__">Unknown</option>
            <option value="__unconfirmed__">Unconfirmed Year</option>
          </select>
          <select value={advFilters.sort_by} onChange={(e) => { setAdvFilters((p) => ({ ...p, sort_by: e.target.value })); setPage(1); }} data-testid="documents-sort-by"
            className="bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-2 text-sm">
            <option value="">Sort: Newest</option>
            <option value="date_asc">Sort: Oldest</option>
            <option value="name_asc">Sort: Name A-Z</option>
            <option value="name_desc">Sort: Name Z-A</option>
            <option value="tax_year_desc">Sort: Tax Year (desc)</option>
            <option value="tax_year_asc">Sort: Tax Year (asc)</option>
          </select>
          <button onClick={() => setShowAdvanced((p) => !p)} data-testid="docs-advanced-filter-toggle"
            className={`inline-flex items-center gap-1.5 text-sm border rounded-lg px-3 py-2 transition-colors ${showAdvanced || activeFiltersCount > 0 ? "border-blue-500 bg-blue-50 text-blue-700" : "border-slate-200 bg-slate-50 text-slate-600 hover:border-slate-300"}`}>
            <Icons.SlidersHorizontal className="h-4 w-4" />Filters
            {activeFiltersCount > 0 && <span className="text-[10px] bg-blue-600 text-white rounded-full px-1.5">{activeFiltersCount}</span>}
          </button>
          <label className="inline-flex items-center gap-1.5 text-sm text-slate-600 cursor-pointer">
            <input type="checkbox" checked={includeArchived} onChange={(e) => setIncludeArchived(e.target.checked)} data-testid="documents-include-archived" />Archived
          </label>
        </div>

        {/* Advanced filters row */}
        {showAdvanced && (
          <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-slate-100" data-testid="documents-advanced-filters">
            <select value={advFilters.doc_type} onChange={(e) => { setAdvFilters((p) => ({ ...p, doc_type: e.target.value })); setPage(1); }} data-testid="documents-filter-doctype"
              className="bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-2 text-sm">
              <option value="">Doc Type: All</option>
              {["W-2", "1099", "1040", "passport", "bank_statement", "corporate_return", "trust_document", "brokerage_statement", "invoice", "contract", "id_document", "other"].map((t) => (
                <option key={t} value={t}>{t.replace(/_/g, " ")}</option>
              ))}
            </select>
            <input
              value={advFilters.jurisdiction}
              onChange={(e) => { setAdvFilters((p) => ({ ...p, jurisdiction: e.target.value })); setPage(1); }}
              placeholder="Jurisdiction…" data-testid="documents-filter-jurisdiction"
              className="bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-2 text-sm w-36" />
            <select value={advFilters.classification_status} onChange={(e) => { setAdvFilters((p) => ({ ...p, classification_status: e.target.value })); setPage(1); }} data-testid="documents-filter-cls-status"
              className="bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-2 text-sm">
              <option value="">Classification: All</option>
              <option value="unclassified">Unclassified</option>
              <option value="low_confidence">Low Confidence</option>
              <option value="high_confidence">High Confidence</option>
              <option value="staff_confirmed">Staff Confirmed</option>
            </select>
            <button onClick={() => { setFilters({ status: "", category: "", source: "", tax_year: "" }); setAdvFilters({ doc_type: "", jurisdiction: "", classification_status: "", sort_by: "" }); setQ(""); setPage(1); }}
              data-testid="docs-clear-filters"
              className="text-xs text-slate-500 hover:text-rose-600 flex items-center gap-1">
              <Icons.X className="h-3.5 w-3.5" />Clear all
            </button>
          </div>
        )}
      </div>

      {/* Year chips */}
      {(data?.year_summary?.length > 0) && (
        <div className="flex flex-wrap items-center gap-1.5" data-testid="documents-year-chips">
          <span className="text-xs font-medium text-slate-400 mr-0.5">Tax Year:</span>
          <button onClick={() => { setFilters((p) => ({ ...p, tax_year: "" })); setPage(1); }} data-testid="documents-year-chip-all"
            className={`text-xs px-3 py-1.5 rounded-full border font-medium transition-colors ${!filters.tax_year ? "bg-[#1C2431] text-white border-[#1C2431]" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}>
            All Years
          </button>
          {data.year_summary.map(({ year, count }) => (
            <button key={year} onClick={() => { setFilters((p) => ({ ...p, tax_year: year })); setPage(1); }}
              data-testid={`documents-year-chip-${year}`}
              className={`text-xs px-3 py-1.5 rounded-full border font-medium transition-colors ${filters.tax_year === year ? "bg-[#1C2431] text-white border-[#1C2431]" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}>
              {YEAR_CHIP_LABEL[year] || year} <span className="opacity-60 ml-0.5">({count})</span>
            </button>
          ))}
        </div>
      )}

      {/* Bulk action toolbar */}
      {selectedIds.size > 0 && (
        <div className="bg-[#1C2431] text-white rounded-xl px-4 py-3 flex items-center gap-3 flex-wrap" data-testid="bulk-action-toolbar">
          <span className="text-sm font-medium">{selectedIds.size} selected</span>
          <div className="flex items-center gap-2 ml-2 flex-wrap">
            <button onClick={() => { setBulkAction("set_metadata"); setBulkMeta({}); setBulkDialogOpen(true); }}
              data-testid="bulk-set-metadata-btn"
              className="inline-flex items-center gap-1.5 text-xs bg-white/10 hover:bg-white/20 rounded-lg px-3 py-1.5">
              <Icons.Tags className="h-3.5 w-3.5" />Set Metadata
            </button>
            <button onClick={() => { setBulkAction("set_tax_year"); setBulkMeta({}); setBulkDialogOpen(true); }}
              data-testid="bulk-set-tax-year-btn"
              className="inline-flex items-center gap-1.5 text-xs bg-white/10 hover:bg-white/20 rounded-lg px-3 py-1.5">
              <Icons.CalendarDays className="h-3.5 w-3.5" />Set Tax Year
            </button>
            <button onClick={() => { setBulkAction("accept_high_confidence"); setBulkDialogOpen(true); }}
              data-testid="bulk-accept-btn"
              className="inline-flex items-center gap-1.5 text-xs bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-200 rounded-lg px-3 py-1.5">
              <Icons.CheckCircle2 className="h-3.5 w-3.5" />Accept High-Confidence
            </button>
            <button onClick={() => { setBulkAction("rerun_processing"); setBulkDialogOpen(true); }}
              data-testid="bulk-reprocess-btn"
              className="inline-flex items-center gap-1.5 text-xs bg-white/10 hover:bg-white/20 rounded-lg px-3 py-1.5">
              <Icons.RefreshCw className="h-3.5 w-3.5" />Re-process
            </button>
          </div>
          <button onClick={clearSelection} className="ml-auto text-xs text-white/60 hover:text-white flex items-center gap-1" data-testid="bulk-clear-selection">
            <Icons.X className="h-3.5 w-3.5" />Clear
          </button>
        </div>
      )}

      {/* table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto">
        <table className="w-full text-sm whitespace-nowrap" data-testid="documents-table">
          <thead>
            <tr className="text-left text-xs text-slate-400 border-b border-slate-100">
              <th className="px-3 py-2.5">
                <input type="checkbox" checked={allSelected} onChange={() => allSelected ? clearSelection() : selectAll()}
                  data-testid="documents-select-all" className="rounded" />
              </th>
              {["Document", "Client", "Category", "Tax Year", "Source", "Status", "Uploaded", "Size", ""].map((h) => (
                <th key={h} className="px-3 py-2.5 font-medium">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && loadError === "forbidden" ? (
              <tr><td colSpan={10} className="px-5 py-12 text-center text-rose-500">
                You do not have permission to access the document repository. Contact your administrator.
              </td></tr>
            ) : items.length === 0 && loadError === "error" ? (
              <tr><td colSpan={10} className="px-5 py-12 text-center text-slate-400">
                Could not load documents. <button onClick={load} className="underline text-blue-500 ml-1">Retry</button>
              </td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={10} className="px-5 py-12 text-center text-slate-400" data-testid="documents-empty">No documents match your filters.</td></tr>
            ) : items.map((d) => (
              <tr key={d.id} data-testid={`documents-row-${d.id}`}
                className={`border-b border-slate-50 hover:bg-slate-50 ${selectedIds.has(d.id) ? "bg-blue-50/60" : ""}`}>
                <td className="px-3 py-2.5">
                  <input type="checkbox" checked={selectedIds.has(d.id)} onChange={() => toggleSelect(d.id)}
                    data-testid={`doc-select-${d.id}`} className="rounded" />
                </td>
                <td className="px-3 py-2.5">
                  <button onClick={() => setPreview(d)} data-testid={`documents-view-${d.id}`}
                    className="text-blue-600 hover:underline flex items-center gap-2 max-w-[260px] truncate text-left">
                    <Icons.FileText className="h-4 w-4 shrink-0 text-slate-400" />
                    <span className="truncate">{d.display_filename}</span>
                    {d.version > 1 && <span className="text-[10px] text-slate-400 shrink-0">v{d.version}</span>}
                    <ProcBadge status={d.processing_status} />
                    <ClsBadge doc={d} />
                    <STRBadge doc={d} />
                    {d.intelligence_state && d.intelligence_state !== "uploaded" && (
                      <IntelligenceStateBadge state={d.intelligence_state} />
                    )}
                  </button>
                </td>
                <td className="px-3 py-2.5 text-slate-700">{d.client_name || "—"}</td>
                <td className="px-3 py-2.5">
                  <select value={d.category || ""} onChange={(e) => changeCategory(d, e.target.value)}
                    data-testid={`documents-cat-${d.id}`} onClick={(e) => e.stopPropagation()}
                    className="bg-transparent text-xs text-slate-600 border border-transparent hover:border-slate-200 rounded px-1 py-0.5">
                    <option value="">—</option>
                    {categories.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                  </select>
                </td>
                <td className="px-3 py-2.5 text-slate-600">
                  {d.tax_year || ((d.staff_confirmed_metadata_fields || []).includes("tax_year")
                    ? <span className="text-[11px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded" data-testid={`documents-tax-year-na-${d.id}`}>N/A</span>
                    : "—")}
                </td>
                <td className="px-3 py-2.5 text-xs text-slate-500">{(d.upload_source || "").replace(/_/g, " ")}</td>
                <td className="px-3 py-2.5">
                  <span className={`text-[11px] px-2 py-0.5 rounded-full ${STATUS_TONE[d.status] || "bg-slate-100"}`}>
                    {(d.status || "").replace(/_/g, " ")}
                  </span>
                </td>
                <td className="px-3 py-2.5 text-slate-500">{fmtDT(d.created_at)}</td>
                <td className="px-3 py-2.5 text-slate-500">{prettySize(d.size)}</td>
                <td className="px-3 py-2.5 text-right">
                  <div className="flex items-center justify-end gap-1">
                    <a href={`${API}/documents-intel/${d.id}/content?disposition=attachment&auth=${encodeURIComponent(getToken() || "")}`}
                      target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}
                      className="p-1.5 rounded hover:bg-slate-100 text-slate-500" title="Download" data-testid={`documents-download-${d.id}`}>
                      <Icons.Download className="h-4 w-4" />
                    </a>
                    <button onClick={() => rename(d)} data-testid={`documents-rename-${d.id}`}
                      className="p-1.5 rounded hover:bg-slate-100 text-slate-500" title="Rename">
                      <Icons.Pencil className="h-4 w-4" />
                    </button>
                    {d.status !== "archived" && (
                      <button onClick={() => archive(d)} data-testid={`documents-archive-${d.id}`}
                        className="p-1.5 rounded hover:bg-slate-100 text-slate-500" title="Archive">
                        <Icons.Archive className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {data && data.total > data.page_size && (
        <div className="flex items-center justify-between text-sm text-slate-500">
          <span>{(page - 1) * data.page_size + 1}–{Math.min(page * data.page_size, data.total)} of {data.total}</span>
          <div className="flex gap-2">
            <button disabled={page === 1} onClick={() => setPage((p) => p - 1)} className="px-3 py-1.5 rounded-lg border border-slate-200 disabled:opacity-40">Prev</button>
            <button disabled={page * data.page_size >= data.total} onClick={() => setPage((p) => p + 1)} className="px-3 py-1.5 rounded-lg border border-slate-200 disabled:opacity-40">Next</button>
          </div>
        </div>
      )}

      {/* Preview drawer */}
      <Sheet open={!!preview} onOpenChange={() => setPreview(null)}>
        <SheetContent className="w-full sm:max-w-2xl overflow-y-auto" data-testid="documents-preview">
          {preview && (
            <>
              <SheetHeader>
                <SheetTitle className="truncate flex items-center gap-2">
                  {preview.display_filename}
                  {preview.display_name_locked && <Icons.Lock className="h-4 w-4 text-emerald-500 shrink-0" title="Display name confirmed by staff" />}
                  {preview.str_evidence && <Icons.Flame className="h-4 w-4 text-rose-500 shrink-0" title="STR Evidence detected" />}
                </SheetTitle>
                <SheetDescription className="sr-only">Document detail</SheetDescription>
              </SheetHeader>

              {/* Intelligence Pipeline State */}
              {preview.intelligence_state && (
                <div className="mt-2 flex items-center gap-1 overflow-x-auto pb-1" data-testid="intelligence-pipeline-bar">
                  {["uploaded","ocr_complete","classified","metadata_extracted","organizer_match_suggested","str_evidence_tagged","business_reviewed","production_ready"].map((state, i) => {
                    const cfg = STR_STATE_ICONS[state];
                    const Ic = Icons[cfg.icon];
                    const stateRank = {"uploaded":0,"ocr_complete":1,"classified":2,"metadata_extracted":3,"organizer_match_suggested":4,"str_evidence_tagged":5,"business_reviewed":6,"production_ready":7};
                    const current = stateRank[preview.intelligence_state] || 0;
                    const thisSt = stateRank[state];
                    const active = thisSt === current;
                    const done = thisSt < current;
                    return (
                      <div key={state} className="flex items-center gap-0.5 shrink-0">
                        {i > 0 && <div className={`h-px w-3 ${done || active ? "bg-slate-400" : "bg-slate-200"}`} />}
                        <div title={cfg.label} className={`flex items-center gap-0.5 text-[10px] px-1.5 py-1 rounded-full ${active ? "bg-slate-900 text-white" : done ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-400"}`}>
                          <Ic className="h-2.5 w-2.5" />{active && <span>{cfg.label}</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Allison Intelligence section */}
              <div className="mt-3 border border-slate-200 rounded-lg overflow-hidden" data-testid="allison-intelligence-section">
                <div className="flex items-center justify-between px-3 py-2 bg-slate-50 border-b border-slate-200">
                  <span className="text-[11px] font-semibold text-slate-600 uppercase tracking-wide flex items-center gap-1.5">
                    <Icons.BrainCircuit className="h-3.5 w-3.5 text-violet-500" />Allison Intelligence
                  </span>
                  <button onClick={runAllisonIntelligence} disabled={allisonRunning}
                    data-testid="run-allison-btn"
                    className="inline-flex items-center gap-1 text-[11px] text-violet-600 hover:text-violet-800 disabled:opacity-50">
                    <Icons.Sparkles className={`h-3 w-3 ${allisonRunning ? "animate-pulse" : ""}`} />
                    {allisonRunning ? "Running…" : "Re-run Allison Intelligence"}
                  </button>
                </div>

                {/* STR Evidence */}
                {(preview.str_evidence || (detail?.document?.str_evidence)) && (
                  <div className="px-3 py-2 border-b border-slate-100" data-testid="str-evidence-section">
                    <div className="flex items-center gap-1.5 mb-1">
                      <Icons.Flame className="h-3.5 w-3.5 text-rose-500" />
                      <span className="text-[11px] font-semibold text-rose-600">STR Evidence Detected</span>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {((detail?.document?.str_evidence_categories || preview.str_evidence_categories) || []).map((cat) => (
                        <span key={cat} className="text-[10px] bg-rose-50 border border-rose-200 text-rose-700 rounded px-1.5 py-0.5">{cat.replace(/_/g, " ")}</span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Organizer match suggestions */}
                {matchLoading ? (
                  <div className="px-3 py-2 text-xs text-slate-400">Loading match suggestions…</div>
                ) : matchSuggestions.length > 0 ? (
                  <div className="px-3 py-2" data-testid="organizer-match-suggestions">
                    <div className="text-[11px] font-semibold text-orange-600 flex items-center gap-1.5 mb-1.5">
                      <Icons.Link className="h-3.5 w-3.5" />Organizer Match Suggested
                    </div>
                    {matchSuggestions.slice(0, 3).map((m) => (
                      <div key={m.request_id || m.id} className="flex items-center justify-between bg-orange-50 border border-orange-200 rounded-md px-2.5 py-2 mb-1">
                        <div>
                          <p className="text-xs font-medium text-slate-700">{m.request_title || m.title}</p>
                          <p className="text-[10px] text-slate-400">{m.match_reason || `Confidence: ${((m.score || m.confidence || 0) * 100).toFixed(0)}%`}</p>
                        </div>
                        <div className="flex items-center gap-1">
                          <button onClick={() => confirmMatch(preview.id, m.request_id || m.id)} data-testid={`confirm-match-${m.request_id || m.id}`}
                            className="text-xs bg-emerald-600 text-white rounded px-2 py-1">Confirm</button>
                          <button onClick={() => rejectMatch(preview.id, m.request_id || m.id)} data-testid={`reject-match-${m.request_id || m.id}`}
                            className="text-xs text-slate-400 hover:text-rose-500 px-1 py-1"><Icons.X className="h-3.5 w-3.5" /></button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="px-3 py-2 text-[11px] text-slate-400">
                    {preview.intelligence_state === "uploaded" || !preview.intelligence_state
                      ? "No intelligence yet — click Re-run Allison Intelligence to analyze this document"
                      : "No organizer match suggestions"}
                  </div>
                )}
              </div>

              {/* Friendly Display Name section */}
              <div className="mt-3 bg-slate-50 border border-slate-200 rounded-lg p-3" data-testid="doc-display-name-section">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide flex items-center gap-1">
                    <Icons.Tag className="h-3 w-3" />Display Name
                    {preview.display_name_locked && (
                      <span className="ml-1 text-[9px] bg-emerald-100 text-emerald-700 rounded px-1 py-0.5 flex items-center gap-0.5">
                        <Icons.Lock className="h-2.5 w-2.5" />Locked by Staff
                      </span>
                    )}
                  </span>
                  <div className="flex items-center gap-1.5">
                    {!preview.display_name_locked && (
                      <button onClick={suggestDisplayName} disabled={allisonRunning} data-testid="doc-suggest-name-btn"
                        className="inline-flex items-center gap-1 text-[11px] text-violet-600 hover:text-violet-800 disabled:opacity-50">
                        <Icons.Sparkles className={`h-3 w-3 ${allisonRunning ? "animate-pulse" : ""}`} />
                        {allisonRunning ? "Generating…" : "AI Suggest"}
                      </button>
                    )}
                    <button onClick={() => setRenameMode((p) => !p)} data-testid="doc-rename-toggle"
                      className="inline-flex items-center gap-1 text-[11px] text-slate-500 hover:text-slate-700">
                      <Icons.Pencil className="h-3 w-3" />Edit
                    </button>
                  </div>
                </div>

                {renameMode ? (
                  <div className="flex items-center gap-2">
                    <input value={renameValue} onChange={(e) => setRenameValue(e.target.value)}
                      data-testid="doc-rename-input"
                      className="flex-1 border border-slate-300 rounded-md px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300" />
                    <button onClick={saveRename} data-testid="doc-rename-save"
                      className="text-xs bg-slate-900 text-white rounded-md px-2.5 py-1.5">Save</button>
                    <button onClick={() => setRenameMode(false)} className="text-xs text-slate-500">Cancel</button>
                  </div>
                ) : (
                  <p className="text-sm text-slate-700 font-medium">{preview.display_filename}</p>
                )}

                {suggestion?.suggested_name && !preview.display_name_locked && (
                  <div className="mt-2 flex items-center gap-2 bg-violet-50 border border-violet-200 rounded-md px-2.5 py-2" data-testid="doc-name-suggestion">
                    <Icons.Sparkles className="h-3.5 w-3.5 text-violet-500 shrink-0" />
                    <div className="flex-1">
                      <p className="text-[11px] text-violet-600 font-medium">AI Suggestion</p>
                      <p className="text-sm text-slate-800">{suggestion.suggested_name}</p>
                    </div>
                    <button onClick={acceptSuggestion} data-testid="doc-accept-suggestion-btn"
                      className="text-xs bg-violet-600 hover:bg-violet-700 text-white rounded px-2 py-1 shrink-0">Accept</button>
                    <button onClick={() => setSuggestion(null)} className="text-slate-400 hover:text-rose-500"><Icons.X className="h-3.5 w-3.5" /></button>
                  </div>
                )}
              </div>

              <div className="mt-3 h-[42vh]">
                <UniversalDocumentViewer docId={preview.id}
                  preview={detail?.canonical?.preview || preview.preview}
                  processingStatus={detail?.canonical?.processing_status || preview.processing_status}
                  displayName={preview.display_filename}
                  onRetry={() => api.post(`/documents-intel/${preview.id}/reprocess`).then(() => toast.message("Reprocessing…"))}
                  className="h-full" />
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                <button onClick={() => navigate(`/documents/${preview.id}`, { state: { returnTo: "/documents" } })}
                  className="inline-flex items-center gap-1.5 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-3 py-2"
                  data-testid="documents-open-full-viewer">
                  <Icons.Maximize2 className="h-4 w-4" />Open full viewer
                </button>
                <a href={`${API}/documents-intel/${preview.id}/content?disposition=attachment&auth=${encodeURIComponent(getToken() || "")}`}
                  target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1.5 text-sm border border-slate-200 rounded-lg px-3 py-2 text-slate-600"
                  data-testid="documents-drawer-download"><Icons.Download className="h-4 w-4" />Download</a>
                {preview.status !== "archived" && (
                  <button onClick={() => archive(preview)}
                    className="inline-flex items-center gap-1.5 text-sm border border-slate-200 rounded-lg px-3 py-2">
                    <Icons.Archive className="h-4 w-4" />Archive
                  </button>
                )}
              </div>

              {(detail?.canonical?.text_extraction || detail?.document?.text_extraction || {}).failure_reason?.includes("ocr_unavailable") && (
                <div className="mt-3 inline-flex items-center gap-1.5 text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5" data-testid="doc-ocr-unavailable">
                  <Icons.ScanLine className="h-3.5 w-3.5" />OCR unavailable — this scanned file is viewable, but no text was extracted.
                </div>
              )}

              <div className="mt-5 grid grid-cols-2 gap-y-2 gap-x-4 text-xs">
                {[
                  ["Category", detail?.document?.category],
                  ["Tax Year", detail?.document?.tax_year],
                  ["Document Type", detail?.document?.document_type],
                  ["Jurisdiction", detail?.document?.jurisdiction],
                  ["Filing Authority", detail?.document?.filing_authority],
                  ["Form Number", detail?.document?.form_number],
                  ["Source", (detail?.document?.upload_source || preview.upload_source || "").replace(/_/g, " ")],
                  ["Version", `v${detail?.document?.version ?? preview.version ?? 1}`],
                  ["Original File", detail?.document?.original_filename],
                  ["Linked Request", detail?.linked_request?.title],
                ].map(([l, v]) => v ? (
                  <div key={l}><div className="text-slate-400">{l}</div><div className="text-slate-700 font-medium capitalize truncate">{v || "—"}</div></div>
                ) : null)}
              </div>

              {detail?.version_history?.length > 1 && (
                <div className="mt-4">
                  <div className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-1.5">Version History</div>
                  <div className="space-y-1">
                    {detail.version_history.map((v) => (
                      <div key={v.id} className="flex items-center justify-between text-xs border border-slate-100 rounded px-2.5 py-1.5">
                        <span className="text-slate-700">v{v.version} · {(v.status || "").replace(/_/g, " ")} {v.is_current && <span className="text-emerald-600">(current)</span>}</span>
                        <span className="text-slate-400">{v.uploaded_by_name} · {fmtDT(v.created_at)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {(detail?.document?.rename_history || detail?.rename_history || []).length > 0 && (
                <div className="mt-4">
                  <div className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-1.5">Rename History</div>
                  <div className="space-y-1">
                    {(detail?.document?.rename_history || detail?.rename_history || []).map((h, i) => (
                      <div key={i} className="text-xs text-slate-500">"{h.from}" → "{h.to}" · {h.by}</div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Bulk Action Dialog */}
      <Dialog open={bulkDialogOpen} onOpenChange={setBulkDialogOpen}>
        <DialogContent className="max-w-md" data-testid="bulk-action-dialog">
          <DialogHeader>
            <DialogTitle>
              {bulkAction === "set_metadata" && "Set Metadata"}
              {bulkAction === "set_tax_year" && "Set Tax Year"}
              {bulkAction === "accept_high_confidence" && "Accept High-Confidence Classifications"}
              {bulkAction === "rerun_processing" && "Re-run Processing"}
            </DialogTitle>
            <DialogDescription>
              {bulkAction === "set_metadata" && `Apply to ${selectedIds.size} selected document${selectedIds.size !== 1 ? "s" : ""}.`}
              {bulkAction === "set_tax_year" && `Assign a canonical tax year to ${selectedIds.size} selected document${selectedIds.size !== 1 ? "s" : ""}. This is a staff-confirmed decision and will mark the tax year as authoritative for Allison and downstream workflows.`}
              {bulkAction === "accept_high_confidence" && `Accept AI classifications meeting the firm's confidence threshold for ${selectedIds.size} selected document${selectedIds.size !== 1 ? "s" : ""}. Only AI-ready documents with no failures and not already staff-confirmed will be accepted.`}
              {bulkAction === "rerun_processing" && `Re-queue ${selectedIds.size} document${selectedIds.size !== 1 ? "s" : ""} for processing. Non-quarantined documents will re-enter the pipeline.`}
            </DialogDescription>
          </DialogHeader>

          {bulkAction === "set_metadata" && (
            <div className="space-y-3 py-2">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-slate-500 font-medium">Category</label>
                  <select value={bulkMeta.category || ""} onChange={(e) => setBulkMeta((p) => ({ ...p, category: e.target.value || undefined }))}
                    data-testid="bulk-meta-category" className="w-full mt-1 border border-slate-200 rounded-lg px-2.5 py-2 text-sm">
                    <option value="">— No change —</option>
                    {categories.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-slate-500 font-medium">Tax Year</label>
                  <input value={bulkMeta.tax_year || ""} onChange={(e) => setBulkMeta((p) => ({ ...p, tax_year: e.target.value || undefined }))}
                    data-testid="bulk-meta-tax-year" placeholder="e.g. 2024"
                    className="w-full mt-1 border border-slate-200 rounded-lg px-2.5 py-2 text-sm" />
                </div>
                <div>
                  <label className="text-xs text-slate-500 font-medium">Jurisdiction</label>
                  <input value={bulkMeta.jurisdiction || ""} onChange={(e) => setBulkMeta((p) => ({ ...p, jurisdiction: e.target.value || undefined }))}
                    data-testid="bulk-meta-jurisdiction" placeholder="e.g. United States"
                    className="w-full mt-1 border border-slate-200 rounded-lg px-2.5 py-2 text-sm" />
                </div>
                <div>
                  <label className="text-xs text-slate-500 font-medium">Filing Authority</label>
                  <input value={bulkMeta.filing_authority || ""} onChange={(e) => setBulkMeta((p) => ({ ...p, filing_authority: e.target.value || undefined }))}
                    data-testid="bulk-meta-filing-authority" placeholder="e.g. IRS"
                    className="w-full mt-1 border border-slate-200 rounded-lg px-2.5 py-2 text-sm" />
                </div>
              </div>
            </div>
          )}

          {bulkAction === "set_tax_year" && (
            <div className="py-2 space-y-2">
              <label className="text-xs text-slate-500 font-medium">Tax Year</label>
              <select
                value={bulkMeta.tax_year || ""}
                onChange={(e) => setBulkMeta({ tax_year: e.target.value || undefined })}
                data-testid="bulk-tax-year-select"
                className="w-full border border-slate-200 rounded-lg px-2.5 py-2 text-sm bg-white"
              >
                <option value="">— Select year —</option>
                {TAX_YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
                <option value="__na__">N/A — Not Tax-Year Specific</option>
              </select>
              <p className="text-[11px] text-slate-400">
                Selecting a year or N/A will mark this as staff-confirmed for all selected documents,
                protecting it from AI extraction. Existing multi-year associations are preserved.
              </p>
            </div>
          )}

          <DialogFooter>
            <button onClick={() => setBulkDialogOpen(false)} className="text-sm px-4 py-2 rounded-lg border border-slate-200" data-testid="bulk-dialog-cancel">Cancel</button>
            <button onClick={executeBulk} disabled={bulkLoading || (bulkAction === "set_tax_year" && !bulkMeta.tax_year)} data-testid="bulk-dialog-confirm"
              className="text-sm px-4 py-2 rounded-lg bg-slate-900 text-white font-medium disabled:opacity-50">
              {bulkLoading ? "Processing…" : "Confirm"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {uploadOpen && <UploadDialog categories={categories} onClose={() => setUploadOpen(false)} onReload={load} />}
    </div>
  );
}

function ProcBadge({ status }) {
  if (!status || status === "ready") return null;
  const map = {
    uploaded: ["Queued", "bg-slate-100 text-slate-500", "Clock"],
    security_scanning: ["Scanning", "bg-indigo-100 text-indigo-600", "ShieldCheck"],
    processing_preview: ["Preview…", "bg-blue-100 text-blue-600", "Loader2"],
    extracting_text: ["Text…", "bg-blue-100 text-blue-600", "Loader2"],
    partially_ready: ["Partial", "bg-amber-100 text-amber-700", "AlertTriangle"],
    processing_failed: ["Failed", "bg-rose-100 text-rose-700", "AlertTriangle"],
    quarantined: ["Quarantined", "bg-rose-100 text-rose-700", "ShieldAlert"],
    pending_security_review: ["Security", "bg-rose-100 text-rose-700", "ShieldAlert"],
  };
  const [label, tone, icon] = map[status] || [status, "bg-slate-100 text-slate-500", "Circle"];
  const Ic = Icons[icon];
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full ${tone}`} data-testid="doc-proc-badge">
      <Ic className={`h-3 w-3 ${icon === "Loader2" ? "animate-spin" : ""}`} />{label}
    </span>
  );
}

function UploadDialog({ categories, onClose, onReload }) {
  const navigate = useNavigate();
  const [category, setCategory] = useState("other");
  const [taxYear, setTaxYear] = useState("");
  const [destType, setDestType] = useState("");
  const [destId, setDestId] = useState("");
  const [q, setQ] = useState("");
  const [contacts, setContacts] = useState([]);
  const [engagements, setEngagements] = useState([]);
  const [anyUploaded, setAnyUploaded] = useState(false);

  useEffect(() => {
    api.get("/crm/contacts", { params: { limit: 500 } }).then((r) => setContacts(r.data?.contacts || r.data || [])).catch(() => {});
    api.get("/engagements").then((r) => setEngagements(r.data || [])).catch(() => {});
  }, []);

  const DESTS = [["client", "Client", "User"], ["lead", "Lead", "UserPlus"], ["engagement", "Engagement", "FileSignature"], ["knowledge", "Knowledge Center", "BookOpen"], ["firm", "Firm Internal Library", "Building2"]];
  const needsEntity = ["client", "lead", "engagement"].includes(destType);
  const options = destType === "engagement"
    ? engagements.filter((e) => !q || `${e.client_name || ""} ${e.engagement_number || e.title || ""}`.toLowerCase().includes(q.toLowerCase())).map((e) => ({ id: e.id, label: `${e.engagement_number || e.title || "Engagement"} — ${e.client_name || ""}` }))
    : contacts.filter((c) => (destType === "lead" ? c.lifecycle_status === "lead" : destType === "client" ? c.lifecycle_status !== "lead" : true))
        .filter((c) => !q || `${c.full_name || ""} ${c.email || ""}`.toLowerCase().includes(q.toLowerCase()))
        .map((c) => ({ id: c.id, label: `${c.full_name}${c.email ? ` · ${c.email}` : ""}` }));

  const uploaderDisabled = !destType || (needsEntity && !destId);

  const uploadFn = useCallback((file, { signal, asVersion, separate, note, onProgress }) => {
    const form = new FormData();
    form.append("file", file);
    form.append("category", category);
    form.append("destination_type", destType);
    if (needsEntity && destId) form.append("destination_id", destId);
    if (taxYear) form.append("tax_year", taxYear);
    if (asVersion) form.append("as_version", "true");
    if (separate) form.append("separate", "true");
    if (note) form.append("version_note", note);
    return api.post("/documents-intel/upload", form, {
      headers: { "Content-Type": "multipart/form-data" }, signal,
      onUploadProgress: (e) => { if (e.total) onProgress(Math.round((e.loaded / e.total) * 100)); },
    }).then((r) => { setAnyUploaded(true); return r.data; })
      .catch((err) => {
        const detail = err.response?.data?.detail;
        if (err.response?.status === 409 && detail?.code === "duplicate_document") {
          const e = new Error("This file was already uploaded — keep as a new version?");
          e.duplicate = detail.existing; throw e;
        }
        throw new Error(formatApiErrorDetail(detail) || "Upload failed.");
      });
  }, [category, destType, destId, needsEntity, taxYear]);

  const pollStatus = useCallback((id) => api.get(`/documents-intel/${id}/status`).then((r) => r.data), []);
  const handleClose = () => { if (anyUploaded) onReload(); onClose(); };

  return (
    <Dialog open onOpenChange={handleClose}>
      <DialogContent className="max-w-lg" data-testid="documents-upload-dialog">
        <DialogHeader><DialogTitle>Upload Documents</DialogTitle><DialogDescription className="sr-only">Choose a destination, then drag & drop or browse to upload one or more files.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium text-slate-500">Upload Destination <span className="text-rose-500">*</span></label>
            <div className="grid grid-cols-1 gap-1.5 mt-1" data-testid="documents-destination">
              {DESTS.map(([key, label, icon]) => {
                const Ic = Icons[icon] || Icons.Circle; const active = destType === key;
                return (
                  <button key={key} type="button" onClick={() => { setDestType(key); setDestId(""); setQ(""); }} data-testid={`dest-${key}`}
                    className={`flex items-center gap-2 text-left rounded-lg border px-3 py-2 text-sm transition-all ${active ? "border-blue-500 bg-blue-50/60 text-blue-800" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}>
                    <Ic className="h-4 w-4" />{label}{active && <Icons.Check className="h-3.5 w-3.5 ml-auto" />}
                  </button>
                );
              })}
            </div>
          </div>
          {needsEntity && (
            <div>
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${destType}…`} data-testid="dest-search" className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm mb-1" />
              <select value={destId} onChange={(e) => setDestId(e.target.value)} size={4} data-testid="dest-entity-select" className="w-full border border-slate-200 rounded-lg px-2 py-1 text-sm">
                {options.slice(0, 100).map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
              </select>
            </div>
          )}
          <div className="grid grid-cols-2 gap-2">
            <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="documents-upload-category" className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm">
              {categories.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
            <input value={taxYear} onChange={(e) => setTaxYear(e.target.value)} placeholder="Tax Year (optional)" data-testid="documents-upload-year" className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm" />
          </div>
          <CanonicalUploader
            uploadFn={uploadFn}
            disabled={uploaderDisabled}
            disabledHint="Select an upload destination first."
            onAllComplete={() => onReload()}
            pollStatus={pollStatus}
            onView={(id) => navigate(`/documents/${id}`, { state: { returnTo: "/documents" } })}
            testId="documents-uploader"
          />
        </div>
        <DialogFooter>
          <button onClick={handleClose} data-testid="documents-upload-close" className="text-sm px-4 py-2 rounded-lg bg-slate-900 text-white font-medium">Done</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
